import { /* BrowserRouter, */ Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useLayoutEffect } from 'react';
import styled from 'styled-components';
import Sidebar from './components/sidebar';
import DemoBanner from './components/DemoBanner';
import DemoTour from './components/DemoTour';
import PageTour from './components/PageTour';
import HelpButton from './components/help/HelpButton';
import { PremiumUpgradeProvider } from './components/premium/PremiumUpgrade';
import axios from 'axios';
import { KontoWPrzerwieProvider, PrzerwaBanner } from './components/przerwa/KontoWPrzerwie';
import Home from './pages/home';
import Reservation from './pages/reservation';
import Inventory from './pages/inventory';
import AddEquipment from './pages/AddEquipment';
import InventorySettings from './pages/InventorySettings';
import Calendar from './pages/calendar';
import Statistics from './pages/statistics';
import Profile from './pages/profile';
import EquipmentDetails from './pages/EquipmentDetails';
import Login from './login/Login';
import YourData from './pages/YourData';
import Security from './pages/Security';
import Users from './pages/Users';
import Preferences from './pages/Preferences';
import AddUser from './pages/AddUser';
import UserDetails from './pages/UserDetails';
import ClientsPage from './pages/clients';
import EquipmentCalendar from './pages/EquipmentCalendar';
import GenerateOffer from './pages/GenerateOffer';
import ActivationPage from './pages/ActivationPage';
import ResetPassword from './pages/ResetPassword';
import SuperAdmin from './pages/SuperAdmin';
import WidgetMedia from './pages/WidgetMedia';
import EmailSettings from './pages/EmailSettings';
import PriceRules from './pages/PriceRules';
import RentalRules from './pages/RentalRules';
import EquipmentBundles from './pages/EquipmentBundles';
import ImportData from './pages/ImportData';
import EksportFirmy from './pages/EksportFirmy';
import ContractSettings from './pages/ContractSettings';
import ContractTemplateBuilder from './pages/ContractTemplateBuilder';
import OfferTemplate from './pages/OfferTemplate';
import PaymentSettings from './pages/PaymentSettings';
import KsefSettings from './pages/KsefSettings';
import PaymentReturn from './pages/PaymentReturn';
import PaymentThankYou from './pages/PaymentThankYou';
import PaymentsList from './pages/PaymentsList';
import Notifications from './pages/Notifications';
import Invoices from './pages/Invoices';
import InvoiceEditor from './pages/InvoiceEditor';
import InvoicePreview from './pages/InvoicePreview';
import PhoneScanner from './pages/PhoneScanner';
import EquipmentLabels from './pages/EquipmentLabels';
import { getInventoryCategoriesStorageKey, getInventorySettingsStorageKey } from './utils/inventoryStorage';
import {
  fetchInventorySettings, saveInventorySettings, fromServerPayload, toServerPayload,
  shouldMigrateLocal, writeLocalCache,
} from './utils/inventorySettingsApi';
import { ToastProvider } from './components/ui/Toast';
import { ConfirmProvider } from './components/ui/Modal';
import { syncSessionFromWhoami, zapiszFlagiSesji } from './utils/sessionSync';
import { isDemoTourSeen } from './utils/demoTour';
import { getPageTour } from './utils/pageTours';
import { API_BASE } from './config';
import { wyloguj } from './utils/wyloguj';
import { ustawTryb } from './utils/theme';

// Motyw: logika przeniesiona do utils/theme.js i wolana w src/index.js przed renderem.
// Tutejsze kopie (`setThemeMode`, `initThemeFromStorage`, `loadUserPreferences`) byly
// MARTWE: `loadUserPreferences` nie bylo wolane ani razu, a `initThemeFromStorage` tylko
// w jego bloku obslugi bledu. To dlatego motyw nie przezywal odswiezenia strony.

function AppWrapper({ children }) {
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    // Sync flag sesji z /api/whoami. Logika i jej kontrakt (flagi kasowane
    // wyłącznie po 401 z serwera, nigdy po błędzie sieci/przerwanym fetchu)
    // w utils/sessionSync.js — tam też testy jednostkowe.
    syncSessionFromWhoami({
      apiBase: API_BASE,
      navigate,
      isMounted: () => mounted,
    });

    // E.2: serwer jest źródłem prawdy dla konfiguracji pól magazynu. Odpala się PRZED
    // syncCategories, żeby ten dalej mógł robić swój lokalny merge na danych, które już
    // odzwierciedlają serwer — inaczej zdepcze świeżo zmigrowany/zcache'owany stan.
    const syncInventorySettings = async () => {
      if (typeof window === 'undefined') return;
      try {
        const data = await fetchInventorySettings();
        if (data.configured) {
          writeLocalCache(fromServerPayload(data));
          return;
        }
        const raw = window.localStorage.getItem(getInventorySettingsStorageKey());
        let local = null;
        try { local = raw ? JSON.parse(raw) : null; } catch (_) { local = null; }
        if (shouldMigrateLocal({ configured: false, localSettings: local })) {
          const payload = toServerPayload(local);
          await saveInventorySettings(payload);
          // normalizuj cache do dokładnie tego, co zwróciłby teraz GET
          writeLocalCache(fromServerPayload({ configured: true, ...payload }));
        }
      } catch (_) {
        // brak sieci / błąd serwera — zostaw cache/defaults, spróbujemy przy następnym starcie
      }
    };

    // Automatyczny sync kategorii z backendu przy starcie aplikacji
    const syncCategories = async () => {
      if (typeof window === 'undefined') return;
      try {
        const res = await fetch(`${API_BASE}/api/equipment-categories`, { credentials: 'include' });
        if (!res.ok) return;
        const rows = await res.json();
        const names = (rows || []).map(r => String(r?.name || '').trim()).filter(Boolean);
        const SETTINGS_KEY = getInventorySettingsStorageKey();
        const CATEGORIES_KEY = getInventoryCategoriesStorageKey();
        const raw = window.localStorage.getItem(SETTINGS_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        const current = parsed.categoryRequirements || {};
        const localFromSettings = Array.isArray(parsed.categories) ? parsed.categories : [];
        const localRaw = window.localStorage.getItem(CATEGORIES_KEY);
        let localFromKey = [];
        try { localFromKey = localRaw ? JSON.parse(localRaw) : []; } catch (_) { localFromKey = []; }
        const mergedNames = Array.from(new Set([
          ...names,
          ...(Array.isArray(localFromSettings) ? localFromSettings : []),
          ...(Array.isArray(localFromKey) ? localFromKey : []),
          ...Object.keys(current || {})
        ].map(v => String(v || '').trim()).filter(Boolean)));

        const nextRequirements = { ...current };
        mergedNames.forEach(name => {
          if (!Array.isArray(nextRequirements[name])) nextRequirements[name] = [];
        });

        const next = { ...parsed, categoryRequirements: nextRequirements, categories: mergedNames };
        try { window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); } catch(_) {}
        // store canonical categories list so other components can read it synchronously
        try { window.localStorage.setItem(CATEGORIES_KEY, JSON.stringify(mergedNames)); } catch(_) {}
      } catch (e) {
        // ignore network errors
      }
    };

    syncInventorySettings().then(syncCategories);
    return () => { mounted = false; };
  }, [navigate]);

  return children;
}

/**
 * Przewiń na gore przy KAZDEJ zmianie strony (klik w sidebarze).
 *
 * React Router NIE robi tego sam — po przejsciu na nowa trase DOM po prostu zostaje
 * tam, gdzie byla przewinieta poprzednia strona, wiec „Nowa rezerwacja" otwierana
 * z dolu przewinietej „Strony glownej" renderowala sie od polowy formularza.
 *
 * Prawdziwym kontenerem przewijania w tej aplikacji NIE jest `window` — `html`/`body`
 * maja `height: 100%` (index.css), wiec to `#root` faktycznie sie przewija i lapie scroll.
 * Zerujemy oba (plus `documentElement`/`body` przez `scrollingElement`) na wszelki
 * wypadek, tym samym zestawem celow, ktorego pojedyncze strony juz uzywaja przy
 * przewijaniu do bledu walidacji (np. `AddEquipment.js#scrollToTop`) — tam ma to byc
 * animowane (`behavior: 'smooth'`), tutaj INSTANT: to ma wygladac jak wejscie na nowa
 * strone, nie jak przewijanie starej.
 *
 * Klucz efektu to `location.pathname`, NIE caly `location` — zmiana samego query stringa
 * (np. filtrow na tej samej stronie) nie powinna szarpac widoku z powrotem do gory.
 *
 * Osobny komponent z efektem „layout", renderowany jako PIERWSZY w układzie (2026-10-02):
 * efekty „layout" biegną w kolejności drzewa, więc reset przewinięcia wykonuje się przed
 * przewodnikiem strony (components/tour/TourOverlay.js), który przewija do podświetlanego
 * elementu. Jako zwykły efekt w App biegł PO nim i cofał stronę na górę, więc element
 * niżej na stronie wypadał z ekranu zaraz po przewinięciu.
 */
function ResetScrollOnRouteChange() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    const root = document.getElementById('root');
    const doc = document.scrollingElement || document.documentElement;
    if (root) root.scrollTop = 0;
    if (doc) doc.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  return null;
}

// Styled components dla layoutu
const AppLayout = styled.div`
  display: flex;
  min-height: 100vh;
`;

const MainContent = styled.div`
  margin-left: ${props => props.$isLoggedIn ? 'var(--sidebar-width, 240px)' : '0'};
  /* Prawa strona i dol licza sie takze na duzym ekranie: tablet w poziomie ma wciecie
     z boku, a kreske gestu u dolu ma niezaleznie od orientacji. Lewa zostaje przy menu,
     bo to ono dotyka lewej krawedzi. */
  /* --help-fab-space ustawia przycisk pomocy (components/help/HelpButton.js): dolny zapas pozwala
     przewinąć ostatnie przyciski strony nad niego, zamiast zostawiać je pod nim. */
  padding: ${props => props.$isLoggedIn
    ? '20px calc(20px + var(--safe-right)) calc(20px + var(--safe-bottom) + var(--help-fab-space, 0px)) 20px'
    : '0'};
  width: ${props => props.$isLoggedIn ? 'calc(100% - var(--sidebar-width, 240px))' : '100%'};
  min-height: 100vh;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    /* Menu jest tu zwiniete, wiec lewa krawedz nalezy do tresci i tez potrzebuje odstepu. */
    padding: ${props => props.$isLoggedIn
      ? '64px calc(12px + var(--safe-right)) calc(12px + var(--safe-bottom) + var(--help-fab-space, 0px)) calc(12px + var(--safe-left))'
      : '0'};
  }
`;

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourChecked, setTourChecked] = useState(false);
  // H.2: licznik wyborów „Przewodnik po tej stronie” w menu przycisku pomocy (PageTour go obserwuje).
  const [pageTourRequest, setPageTourRequest] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Sprawdź czy jesteśmy na stronie publicznej (bez sidebara)
  const isActivationPage = location.pathname.startsWith('/activate/');
  const isResetPasswordPage = location.pathname.startsWith('/reset-password/');
  const isPublicPaymentPage = location.pathname.startsWith('/payments/thank-you');
  // Strona telefonu sparowanego z komputerem — publiczna i BEZ menu także wtedy, gdy na tym
  // telefonie ktoś jest akurat zalogowany do panelu (sidebar zasłaniałby podgląd aparatu).
  const isPhoneScanPage = location.pathname.startsWith('/scan/');
  const isChromeless = isActivationPage || isResetPasswordPage || isPublicPaymentPage || isPhoneScanPage;

  useEffect(() => {
    const checkAuth = async () => {
      setAuthLoading(true);
      try {
        // Sprawdź czy mamy dane w localStorage
        const storedUserId = localStorage.getItem('userId');
        const storedIsAdmin = localStorage.getItem('isAdmin');
        const storedIsLoggedIn = localStorage.getItem('isLoggedIn');

        // Jeśli brak flagi logowania - przekieruj na login
        if (storedIsLoggedIn !== 'true') {
          setIsLoggedIn(false);
          setAuthLoading(false);
          return;
        }

        // Zweryfikuj sesję po stronie serwera
        const response = await fetch(`${API_BASE}/api/whoami`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          // Sesja nieważna - wyczyść localStorage i przekieruj na login
          localStorage.removeItem('userId');
          localStorage.removeItem('isAdmin');
          localStorage.removeItem('isLoggedIn');
          localStorage.removeItem('csrfToken');
          setIsLoggedIn(false);
          setAuthLoading(false);
          return;
        }

        const data = await response.json();
        
        // Weryfikuj zgodność danych z localStorage
        if (data.userId && String(data.userId) === String(storedUserId)) {
          setUser({
            userId: data.userId,
            isAdmin: data.user?.isAdmin || storedIsAdmin === '1',
            username: data.user?.firstname || '',
            ...data.user
          });
          setIsLoggedIn(true);
          
          // Zaktualizuj csrfToken jeśli przyszedł z serwera
          if (data.csrfToken) {
            localStorage.setItem('csrfToken', data.csrfToken);
          }
          // Synchronizuj flagi uprawnień, organizacji i planu zgodnie z odpowiedzią serwera
          zapiszFlagiSesji(localStorage, data.user);
        } else {
          // Niezgodność danych - wyloguj
          localStorage.removeItem('userId');
          localStorage.removeItem('isAdmin');
          localStorage.removeItem('isLoggedIn');
          localStorage.removeItem('csrfToken');
          setIsLoggedIn(false);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // Błąd połączenia ≠ nieważna sesja — flagi w localStorage zostają
        // (kasować wolno tylko po 401 z serwera, gałęzie wyżej). Fetch bywa
        // odrzucany przez unload podczas pełnej nawigacji; wyczyszczenie tu
        // flag wylogowałoby użytkownika w następnym oknie mimo ważnej sesji.
        setIsLoggedIn(false);
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Przewodnik demo: otwieramy raz, przy pierwszym wejściu właściciela org demo.
  // `tourChecked` pilnuje, żeby ponowne zamknięcie nie wywołało auto-otwarcia
  // jeszcze raz w tej samej sesji (stan „widziane" leci do localStorage w DemoTour).
  useEffect(() => {
    if (!isLoggedIn || !user?.isDemo || tourChecked) return;
    setTourChecked(true);
    if (!isDemoTourSeen(window.localStorage, user.userId)) setTourOpen(true);
  }, [isLoggedIn, user, tourChecked]);

  // Funkcja handleLogin - zaktualizuj aby ustawiać flagę isLoggedIn
  // Motyw KONKRETNEGO uzytkownika, pobierany po zalogowaniu.
  //
  // Start aplikacji naklada motyw z localStorage (src/index.js), zeby nie bylo mignięcia ani
  // powrotu do jasnego po odswiezeniu. To jednak za malo w dwoch przypadkach, oba znalezione
  // w przegladzie kodu: na WSPOLDZIELONEJ przegladarce kolejny uzytkownik dziedziczyl motyw
  // poprzednika, a na nowym urzadzeniu localStorage jest puste i wybor z serwera pojawial sie
  // dopiero po wejsciu w Ustawienia.
  //
  // Rozwiazaniem NIE jest czyszczenie motywu przy wylogowaniu (wtedy ten sam czlowiek traci swoj
  // wybor przy kazdym wylogowaniu) ani rezygnacja z zapisu (to przywrociloby usterke: po
  // odswiezeniu znowu jasny). Zrodlem prawdy jest serwer i stad go bierzemy.
  useEffect(() => {
    if (!isLoggedIn) return;
    let aktualne = true;
    fetch(`${API_BASE}/api/preferences`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((prefs) => {
        if (aktualne && prefs && prefs.theme) ustawTryb(prefs.theme);
      })
      // Brak sieci albo blad odczytu zostawia motyw nalozony przy starcie, nie gasi aplikacji.
      .catch(() => {});
    return () => { aktualne = false; };
  }, [isLoggedIn]);

  const handleLogin = (userData) => {
    localStorage.setItem('userId', userData.userId);
    localStorage.setItem('isAdmin', userData.isAdmin ? '1' : '0');
    localStorage.setItem('isSuperAdmin', userData.isSuperAdmin ? '1' : '0');
    localStorage.setItem('organizationId', userData.organizationId || '');
    localStorage.setItem('isLoggedIn', 'true');
    
    setUser(userData);
    setIsLoggedIn(true);
  };

  // Funkcja handleLogout - upewnij się że czyści wszystkie dane
  const handleLogout = wyloguj;

  // W return - dodaj przekierowanie dla niezalogowanych
  if (authLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'var(--panel-bg)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid rgba(59,130,246,0.2)',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: 'var(--text)', fontSize: '1.1rem' }}>
            Sprawdzanie sesji...
          </p>
        </div>
      </div>
    );
  }

  return (
      <AppLayout>
        {/* F.2: jedno okno „Chcę Premium” dla menu, stron i komunikatów po odmowie planu. */}
        <PremiumUpgradeProvider user={user}>
        {/* F.11: przerwa w sezonie. Stan z sesji i z odmów serwera, baner i blokada przycisków zapisu. */}
        <KontoWPrzerwieProvider user={isLoggedIn ? user : null} axiosInstance={axios}>
        <ResetScrollOnRouteChange />
        {isLoggedIn && !isChromeless && <Sidebar />}
        <MainContent $isLoggedIn={isLoggedIn && !isChromeless}>
          {isLoggedIn && !isChromeless && (
            <>
              <DemoBanner user={user} onShowTour={() => setTourOpen(true)} />
              <PrzerwaBanner />
              {user?.isDemo && (
                <DemoTour open={tourOpen} onClose={() => setTourOpen(false)} userId={user?.userId} />
              )}
              {/* Mini-przewodniki stron. Same otwierają się tylko w demo, a z menu pomocy (H.2) dla
                  każdego zalogowanego. Wstrzymane, dopóki trwa spacer albo w demo nie wiadomo jeszcze,
                  czy się otworzy (`tourChecked` ustawia się wyłącznie dla kont demo). */}
              <PageTour
                user={user}
                mainTourOpen={tourOpen}
                waitForMainTour={!!user?.isDemo && !tourChecked}
                openRequest={pageTourRequest}
              />
              {/* H.2: stały przycisk „?” (zgłoszenia i przewodnik strony) na każdej stronie panelu. */}
              <HelpButton
                user={user}
                pageTourAvailable={!!getPageTour(location.pathname) && !tourOpen && !(user?.isDemo && !tourChecked)}
                onOpenPageTour={() => setPageTourRequest((n) => n + 1)}
              />
            </>
          )}
          <Routes>
            {/* Publiczne route */}
            <Route path="/activate/:token" element={<ActivationPage />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/payments/thank-you" element={<PaymentThankYou />} />
            <Route path="/scan/:token" element={<PhoneScanner />} />

            {!isLoggedIn ? (
              <Route path="*" element={<Login onLogin={handleLogin} />} />
            ) : (
              <>
                <Route path="/home" element={<Home />} />
                <Route path="/reservation" element={<Reservation />} />
                <Route path="/inventory" element={<Inventory />} />
                <Route path="/inventory/add" element={<AddEquipment />} />
                <Route path="/inventory/add-category" element={<InventorySettings />} />
                <Route path="/inventory/labels" element={<EquipmentLabels />} />
                <Route path="/inventory/:id" element={<EquipmentDetails />} />
                <Route path="/inventory/:id/calendar" element={<EquipmentCalendar />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/clients" element={<ClientsPage />} />
                <Route path="/statistics" element={<Statistics />} />
                <Route path="/generate-offer" element={<GenerateOffer />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/your-data" element={<YourData />} />
                <Route path="/security" element={<Security />} />
                <Route path="/users" element={<Users />} />
                <Route path="/preferences" element={<Preferences />} />
                <Route path="/add-user" element={<AddUser />} />
                <Route path="/users/:id" element={<UserDetails />} />
                <Route path="/users/:id/edit" element={<UserDetails />} />
                <Route path="/super-admin" element={
                  (user?.isSuperAdmin || localStorage.getItem('isSuperAdmin') === '1')
                    ? <SuperAdmin />
                    : <Navigate to="/home" replace />
                } />
                <Route path="/email-settings" element={<EmailSettings />} />
                <Route path="/price-rules" element={<PriceRules />} />
                <Route path="/rental-rules" element={<RentalRules />} />
                <Route path="/equipment-bundles" element={<EquipmentBundles />} />
                <Route path="/import" element={<ImportData />} />
                <Route path="/company-export" element={<EksportFirmy />} />
                <Route path="/contract-settings" element={<ContractSettings />} />
                <Route path="/contract-template" element={<ContractTemplateBuilder />} />
                <Route path="/offer-template" element={<OfferTemplate />} />
                <Route path="/payment-settings" element={<PaymentSettings />} />
                <Route path="/ksef-settings" element={<KsefSettings />} />
                <Route path="/payments" element={<PaymentsList />} />
                <Route path="/payments/return" element={<PaymentReturn />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/invoices" element={<Invoices />} />
                <Route path="/invoices/new" element={<InvoiceEditor mode="create" />} />
                <Route path="/invoices/:id/edit" element={<InvoiceEditor mode="edit" />} />
                <Route path="/invoices/:id" element={<InvoicePreview />} />
                {(() => {
                  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === '1';
                  const isAdmin = user?.isAdmin || localStorage.getItem('isAdmin') === '1' || isSuperAdmin;
                  const subscriptionPlan = localStorage.getItem('subscriptionPlan') || 'basic';
                  const hasWidgetAccess = isSuperAdmin || (isAdmin && subscriptionPlan === 'premium');
                  return hasWidgetAccess && <Route path="/widget-media" element={<WidgetMedia />} />;
                })()}
                {/* Przekierowanie domyślne */}
                <Route path="*" element={<Navigate to="/home" />} />
              </>
            )}
          </Routes>
        </MainContent>
        </KontoWPrzerwieProvider>
        </PremiumUpgradeProvider>
      </AppLayout>
  );
}

/**
 * `ToastProvider`/`ConfirmProvider` owijaja CALA aplikacje, nie tylko widok zalogowany —
 * strony publiczne (`PaymentThankYou`, `ActivationPage`, `ResetPassword` — `PaymentReturn`
 * mimo nazwy jest WEWNATRZ bloku `isLoggedIn`, wiec wymaga sesji) tez renderuja sie pod
 * `<App/>` i tez moga miec formularze/potwierdzenia wymagajace tych hookow. `useToast`/
 * `useConfirm` maja wlasny bezpiecznik na brak providera, ale lepiej go po prostu nie potrzebowac.
 */
export default function Root() {
  return (
    <ToastProvider>
      <ConfirmProvider>
        <AppWrapper>
          <App />
        </AppWrapper>
      </ConfirmProvider>
    </ToastProvider>
  );
}